import axios from 'axios';
import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

// base.json ＋ locale辞書 → 画面用オブジェクト配列
function mergeBaseAndLocale(baseList = [], dict = {}) {
  const locName    = dict?.penguin?.name    || {};
  const locComment = dict?.penguin?.comment || {};
  const locPlace   = dict?.place            || {};
  const locGender  = dict?.gender           || {};
  const locColor   = dict?.color            || {};

  const normalizeGender = (g) => (g === 'male' || g === 'female') ? g : 'unknown';

  const buildBirthdayLabel = (b) => {
    // 表示にはこの文字列を渡す。i18n 側の `... { date }` に入れる用途想定
    if (b.birthday_iso) return b.birthday_iso;            // 例: "1999-02-25"
    if (b.birth_year)  return String(b.birth_year);       // 例: "1997"
    return '';
  };

  return baseList.map(b => {
    const gender = normalizeGender(b.gender);

    return {
      // サーバからの生データも（score等）落とさないようにまず展開
      ...b,

      // 識別子 & 検索用
      id: b.id,
      avr_pos: b.avr_pos,

      // 画像
      picture: b.picture,
      icon: b.icon,

      // 名前/ペア（表示ラベル）
      nameLabel: locName[b.id] ?? b.id ?? '',
      pairLabel: b.pair_id ? (locName[b.pair_id] ?? b.pair_id) : '',

      // 色：CSSで塗るために“生ID”も保持＋ラベルも別で用意
      band_color: b.band_color || [],                                 // 例: ["orange","lime"]
      band_color_label: (b.band_color || []).map(c => locColor[c] || c),

      // 場所：内部IDも残しつつ、表示ラベルも作る
      placeId: b.place_id || '',
      placeLabel: b.place_id ? (locPlace[b.place_id] || b.place_id) : '',

      // 性別：内部値＋ラベル
      gender,
      // genderLabel: locGender[gender] || '',
      genderLabel: (gender !== 'unknown') ? (locGender[gender] || '') : '',

      // 誕生日：ISO/精度/年を残しつつ、画面に渡すシンプル文字列も用意
      birthdayISO: b.birthday_iso || '',
      birthdayPrecision: b.birthday_precision || '',
      birthYear: b.birth_year ?? null,
      birthdayLabel: buildBirthdayLabel(b),

      // コメント（なければ空文字でOK）
      comment: locComment[b.id] || '',
    };
  });
}




export default new Vuex.Store({
  state: {
    aquarium: '',
    penName: '',
    aquaId: 1,
    bandColor: [],
    imgUrl: null,
    drawTime: 0,
    data: null,
    rank_data: [],
    rank_base_last: [], //検索APIが返したbase 形（id, *_key …）の生データを保存。localizeAndSetRankData でまずここに保存してから、mergeBaseAndLocale で rank_data を作る。
    // penguinsByAquarium: {}, // { sunshine: [merged penguins] }（図鑑で使うローカライズ済み一覧のキャッシュ）
    part: [1, 1, 1, 1, 1, 1, 1, 1, 1],
    user: {
      name: 'guest',
      uid: 'guest'
    },
    penguins: [], // 現在の水族館のローカライズ済み一覧
    // ▼ 追加：ローカライズ辞書と逆引き/インデックス
    penguinLocaleDict: null, // 現在のロケール（ja/en/ko/zh）に対応する翻訳辞書を保持。
    baseById: null, // base.jsonをid→レコードで引ける辞書にしたもの。詳細やアルバムでidから1件だけをすぐ引ける。
    jaNameToId: null, // 昔のDBやURLパラメータに日本語名だけが保存されている場合に、id を引き直すために使う。アルバム旧データ救済用（任意）
    localeStamp: null,        // 現在ロード済み辞書のロケール
    lastMergedLocale: null,   // 直近で penguins を作ったときのロケール
    search_id: null,         // 現在の検索セッションID（Ranking.vue で設定）
  },
  actions: {
    async sendUserData(context, user) {
      if (!user?.name || !user?.uid) {
        return
      }
      const {
        name,
        uid
      } = user
      // console.log("Sending data:", { name, uid }); // 送信するデータをログに出力して確認
      try {
        const response = await axios.post('https://nakagawa.nkmr.io/api/pensaku/post_user.php', {
          name: name,
          uid: uid
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.success) {
          // console.log("ユーザデータが登録されました。UID:", response.data);
        } else {
          // console.error("Unexpected response format:", response.data);
        }
      } catch (error) {
        console.error("ユーザデータの送信に失敗しました。", error);
        if (error.response) {
          console.error("Server responded with status:", error.response.status);
          // console.error("Response data:", error.response.data);
        }
      }
    },

    // ▼ 追加：ローカル辞書ロード（起動時＆ロケール変更時に呼ぶ）
    async loadPenguinLocale({
      state,
      commit
    }, locale) {
      if (!state.aquarium) return; // aquarium 未設定ならスキップ
      const url = `https://nakagawa.nkmr.io/api/pensaku/${state.aquarium}/localize/${locale}.json`;
      const dict = await axios.get(url).then(r => r.data);
      commit('setPenguinLocaleDict', { dict, locale });
    },

    // ▼ 追加：base.json を取得して id インデックスを用意（図鑑/アルバム向け）
    async loadPenguinBase({
      state,
      commit
    }) {
      if (!state.aquarium) return;
      const url = `https://nakagawa.nkmr.io/api/pensaku/${state.aquarium}/base.json`;
      const baseList = await axios.get(url).then(r => r.data);
      const byId = Object.fromEntries(baseList.map(p => [p.id, p]));
      commit('setBaseById', byId);
      return baseList;
    },

    // ▼ 追加：JA辞書から逆引き作成（アルバムの“日本語名→id”救済用）
    async buildJaReverseMap({
      state,
      commit
    }) {
      if (!state.aquarium) return;
      const url = `https://nakagawa.nkmr.io/api/pensaku/${state.aquarium}/localize/ja.json`;
      const jaDict = await axios.get(url).then(r => r.data);
      const map = Object.fromEntries(Object.entries(jaDict?.penguin?.name || {}).map(([id, ja]) => [ja, id]));
      commit('setJaNameToId', map);
    },

    // ▼ 追加：APIから返った base レコード配列をローカライズして store へ
    localizeAndSetRankData({ state, commit }, baseList) {
      commit('setRankBaseLast', baseList); // 生を保持
      const dict = state.penguinLocaleDict || {};
      const localized = mergeBaseAndLocale(baseList, dict);
      commit('setRankData', { rank_data: localized });
    },

    // その水族館の base + 現在localeをまとめてロード（図鑑が最初に呼ぶ）
    async ensureAquariumLoaded({ state, commit, dispatch }, aquarium) {
      // 1) まず対象水族館を state に反映（loadPenguinBase が state.aquarium を参照するため）
      if (!state.aquarium || state.aquarium !== aquarium) {
        commit('setAquarium', { aquarium });
      }

      // 2) 既にローカライズ済み一覧があれば終了（軽量化）
      if (Array.isArray(state.penguins) && state.penguins.length > 0 && state.penguinLocaleDict) {
        return;
      }

      // 3) base.json が未ロードならロード（byId を作る）
      if (!state.baseById) {
        await dispatch('loadPenguinBase');  // ← state.aquarium を使って取得＆ setBaseById
      }

      // 4) locale 辞書が無ければ、現在ロケール（localStorage か ja）でロード
      const loc = localStorage.getItem('locale') || 'ja';
      if (!state.penguinLocaleDict) {
        await dispatch('loadPenguinLocale', loc);
      }

      // 5) base + locale を合体して UI 用の配列にして保存
      const baseList = Object.values(state.baseById || []);
      const merged = mergeBaseAndLocale(baseList, state.penguinLocaleDict || {});
      commit('setPenguins', merged);
    },

    // // 指定ロケールで図鑑を再構築
    // async loadAquariumForLocale({
    //   state,
    //   commit,
    //   dispatch
    // }, {
    //   aquarium,
    //   locale
    // }) {
    //   // base が無ければ先に取得
    //   if (!state.baseById) {
    //     await dispatch('loadPenguinBase');
    //   }
    //   // locale 辞書が無ければ取得（引数 locale が null の時は state のロケールでOK）
    //   if (!state.penguinLocaleDict || locale) {
    //     const loc = locale || (localStorage.getItem('locale') || 'ja');
    //     await dispatch('loadPenguinLocale', loc);
    //   }
    //   // base.json は byId で持っているので配列へ戻す
    //   const baseList = Object.values(state.baseById || {});
    //   const merged = mergeBaseAndLocale(baseList, state.penguinLocaleDict);
    //   commit('setPenguinsForAquarium', {
    //     aquarium,
    //     list: merged
    //   });
    // },

    async rebuildPenguins({ state, commit }) {
      if (!state.baseById || !state.penguinLocaleDict) return;
      const baseList = Object.values(state.baseById);
      const merged = mergeBaseAndLocale(baseList, state.penguinLocaleDict);
      commit('setPenguins', merged); // setPenguins 内で lastMergedLocale を更新
    }
  },
  mutations: {
    setName(state, val) {
      state.name = val.name
    },
    setMail(state, val) {
      state.mail = val.mail
    },
    setAquarium(state, val) {
      state.aquarium = val.aquarium
    },
    setPenName(state, val) {
      state.penName = val.penName
    },
    setBandcolor(state, val) {
      state.bandColor = val.bandColor
    },
    setRankData(state, val) {
      state.rank_data = val.rank_data
    },
    setUser(state, user) {
      state.user = user
    },
    setPenguinLocaleDict(state, { dict, locale }) {
      state.penguinLocaleDict = dict;
      state.localeStamp = locale; // ←今の辞書が何語かを刻む
    },
    setBaseById(state, byId) {
      state.baseById = byId
    },
    setJaNameToId(state, map) {
      state.jaNameToId = map
    },
    setRankBaseLast(state, list) {
      state.rank_base_last = Array.isArray(list) ? list : [];
    },
    setPenguins(state, list) {
      state.penguins = list;
      state.lastMergedLocale = state.localeStamp; // いま作ったのはこのロケールで、という印
    },
    // setPenguinsForAquarium(state, { aquarium, list }) {
    //   if (!state.penguinsByAquarium) state.penguinsByAquarium = {};
    //   state.penguinsByAquarium = {
    //     ...state.penguinsByAquarium,
    //     [aquarium]: Array.isArray(list) ? list : []
    //   };
    // }
  },
  getters: {
    // // 図鑑（List）やアルバム、詳細でローカライズ済み一覧を取得
    // // 引数として aquarium（例: "sunshine"）を受け取って、state.penguinsByAquarium[aquarium] に保存されているペンギンリストを返す（なければ空[]を返すよ）
    // // this.$store.getters.penguinsForAquarium('sunshine') のように使う
    // penguinsForAquarium: (state) => (aquarium) =>
    //   state.penguinsByAquarium[aquarium] || [],

    // // ストアのpenguinsByAquariumに今読み込んでいる水族館のキー一覧を返す
    // loadedAquariums: (state) => Object.keys(state.penguinsByAquarium || {}),


    penguins(state) {
      return state.penguins
    }
  },
  modules: {}
})

// /** ▼ 追加：ローカライズ関数（小さいのでストア末尾に同居） */
// function localizeOne(base, dict) {
//   const nameLabel = dict?.penguin?.name?.[base.id] ?? base.id ?? base.name ?? '';
//   const pairLabel = base.pair_id ? (dict?.penguin?.name?.[base.pair_id] ?? base.pair_id) : (base.pair || '');
//   const placeLabel = dict?.place?.[base.place] ?? base.place ?? '';
//   // 誕生日：ISOがあればそれ, なければ既存文字列
//   const birthdayLabel = base.birthday_iso || base.birthday || '';

//   return {
//     ...base,
//     nameLabel,
//     pairLabel,
//     placeLabel,
//     birthdayLabel,
//   };
// }